import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, switchMap, map, tap } from 'rxjs/operators';
import {
	UserActionTypes,
	GetMe,
	GetMeSuccess,
	GetMeFailure,
	GetAllUsers,
	GetAllUsersSuccess,
	GetAllUsersFailure,
	AddUser,
	AddUserSuccess,
	AddUserFailure,
	UpdateUser,
	UpdateUsers,
	UpdateUserSuccess,
	UpdateUsersSuccess,
	UpdateUserFailure,
	DeactivateUser,
	DeactivateUserSuccess,
	DeactivateUserFailure,
	SetDefaultSiteForUser,
	SetDefaultSiteForUserSuccess,
	SetDefaultSiteForUserFailure,
	AcceptEula,
	AcceptEulaSuccess,
	AcceptEulaFailure,
} from '../actions/user.actions';
import { UserService } from '../../user.service';
import { User, NewUser } from '../../user.interfaces';
import { ToastService } from '../../../toast/toast.service';
import { LoginSuccess } from '../../../auth/store/actions/auth.actions';
import { GetSite } from 'src/app/site/store/actions/site.actions';


@Injectable()
export class UserEffects {
	loadUser$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<GetMe>(UserActionTypes.GetMe),
		switchMap(() => {
			return this.userService.getSelf().pipe(
				switchMap(user => {
					return [new GetMeSuccess(user), new LoginSuccess()];
				}),
				tap(action => {
					if (action.type === UserActionTypes.GetMeSuccess) {
						localStorage.setItem('mapkit_token', action.user.mapKitToken);
					}
				}),
				catchError((error, caught) => of(new GetMeFailure(error)))
			);
		}))
	);

	getAllUsers$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<GetAllUsers>(UserActionTypes.GetAllUsers),
		map(action => action.siteId),
		switchMap(siteId => {
			return this.userService.getAllUsers(siteId).pipe(
				map((users: User[]) => {
					return new GetAllUsersSuccess(users);
				}),
				catchError(error => of(new GetAllUsersFailure(error)))
			);
		}))
	);

	addUser$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<AddUser>(UserActionTypes.AddUser),
		map(action => action.user),
		switchMap(user => {
			return this.userService.addUser(user).pipe(
				map((newUser: User) => {
					this.toastService.addMessage({ text: `User "${newUser.firstName} ${newUser.lastName}" was successfully added.`, error: false });
					return new AddUserSuccess(newUser);
				}),
				catchError(error => {
					this.toastService.addMessage({ text: `${error}`, error: true });
					return of(new AddUserFailure(error));
				})
			);
		}))
	);

	updateUser$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<UpdateUser>(UserActionTypes.UpdateUser),
		switchMap(action => {
			return (this.userService.updateUser(action.userId, action.user)).pipe(
				map((updatedUser: User) => {
					this.toastService.addMessage({ text: `User "${updatedUser.firstName} ${updatedUser.lastName}" was successfully updated.`, error: false });
					return new UpdateUserSuccess(updatedUser);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: `An error occured while updating user "${action.user.firstName} ${action.user.lastName}". Save Failed.`,
						error: true,
					});
					return of(new UpdateUserFailure(error));
				})
			);
		}))
	);

	updateUsers$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<UpdateUsers>(UserActionTypes.UpdateUsers),
		switchMap(action => {
			return this.userService.updateUsers(action.users).pipe(
				map((updatedUser: NewUser[]) => {
					if (action.users.length > 1){
						this.toastService.addMessage({ text: `${action.users.length} users were successfully updated.`, error: false });
					} else {
						let updatedUser = action.users[0];
						let originalUser = action.unmodifiedUsers[updatedUser.id];
						let previousRole = 'Reader';
						let message = 'User was successfully updated';

						if (action.roleName == 'admin') {
							// if they were an admin and arent anymore
							if (!updatedUser.site.userIsAdmin) {
								message = `${updatedUser.firstName} was previously an Admin and is now a Reader`;
							} else 
							// if they were something else but are now an admin
							{
								previousRole = originalUser.sites[0].userIsContributor ? 'Contributor' : 'Reader';
								message = `${updatedUser.firstName} was previously a ${previousRole} and is now a Admin`;
							}
						} else if (action.roleName == 'contributor') {
							// if they were a contributor but arent anymore
							if (!updatedUser.site.userIsContributor) {
								message = `${updatedUser.firstName} was previously an Contributor and is now a Reader`;
							} else 
							// if they were something else but are now a contributor
							{
								previousRole = originalUser.sites[0].userIsAdmin ? 'Admin' : 'Reader';
								message = `${updatedUser.firstName} was previously a ${previousRole} and is now a Contributor`;
							}
						} else {
							// Question: what happens if they uncheck the reader box?

							// if they were something else but are now a reader
							if (!updatedUser.site.userIsAdmin && !updatedUser.site.userIsContributor) {
								previousRole = originalUser.sites[0].userIsAdmin ? 'Admin' : 'Contributor';
								message = `${updatedUser.firstName} was previously a ${previousRole} and is now a Reader`;
							}
						}

						this.toastService.addMessage({ text: message, error: false });
					}

					return new UpdateUsersSuccess(updatedUser);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: `An error occured while updating users. Save Failed.`,
						error: true,
					});
					return of(new UpdateUserFailure(error));
				})
			)
		}))
	);

	setDefaultSiteForUser$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<SetDefaultSiteForUser>(UserActionTypes.SetDefaultSiteForUser),
		map(action => action.siteId),
		switchMap(siteId => {
			return this.userService.setDefaultSiteForUser(siteId).pipe(
				switchMap((user: User) => {
					this.toastService.addMessage({ text: `Your default site has been updated!`, error: false });
					return of(new SetDefaultSiteForUserSuccess(user));
				}),
				catchError(error => {
					this.toastService.addMessage({ text: `An error occured while updating your default site. Save Failed.`, error: true });
					return of(new SetDefaultSiteForUserFailure(error));
				})
			);
		}))
	);

	deleteUser$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<DeactivateUser>(UserActionTypes.DeactivateUser),
		map(action => action),
		switchMap(action => {
			return this.userService.deactivateUser(action.userId, action.siteId).pipe(
				map((user: any) => {
					this.toastService.addMessage({ text: `User was deleted.`, error: false });
					return new DeactivateUserSuccess(action.userId);
				}),
				catchError(error => {
					this.toastService.addMessage({ text: `An error occured while deleting user. Deletion Failed.`, error: true });
					return of(new DeactivateUserFailure(error));
				})
			);
		}))
	);

	acceptEula$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<AcceptEula>(UserActionTypes.AcceptEula),
		switchMap(action => {
			return this.userService.acceptEula().pipe(
				switchMap(() => {
					return of(new AcceptEulaSuccess());
				}),
				catchError(error => {
					this.toastService.addMessage({ text: `An error occured while accepting the EULA. Please refresh and try again.`, error: true });
					return of(new AcceptEulaFailure(error));
				})
			);
		}))
	);

	constructor(private actions$: Actions, private userService: UserService, public toastService: ToastService) {}
}
